import type { Activity, Goal, Sex, Targets, Track } from './types';

const ACTIVITY: Record<Activity, number> = { sedentary: 1.25, light: 1.35, moderate: 1.5, active: 1.65 };

export const ACTIVITY_LABEL: Record<Activity, string> = {
  sedentary: 'Desk job, little walking',
  light: 'Desk job + 2–3 light classes a week',
  moderate: 'On your feet often, or 3–4 workouts a week',
  active: 'Physical job, or hard training most days',
};

/** Mifflin–St Jeor, or the BMR from a body-composition scale when the person has one. */
export function bmr(sex: Sex, weightKg: number, heightCm: number, age: number, scaleBmr?: number | null): number {
  if (scaleBmr && scaleBmr > 800) return scaleBmr;
  return 10 * weightKg + 6.25 * heightCm - 5 * age + (sex === 'male' ? 5 : -161);
}

const round50 = (n: number) => Math.round(n / 50) * 50;

export function computeTargets(p: {
  sex: Sex;
  weightKg: number;
  heightCm: number;
  birthYear: number;
  activity: Activity;
  goal: Goal;
  scaleBmr?: number | null;
}): Targets {
  const age = new Date().getFullYear() - p.birthYear;
  const tdee = bmr(p.sex, p.weightKg, p.heightCm, age, p.scaleBmr) * ACTIVITY[p.activity];
  const deficit = p.goal === 'lose' ? (p.sex === 'male' ? 575 : 300) : 0;
  const floor = p.sex === 'male' ? 1600 : 1300;
  const kcal = Math.max(floor, round50(tdee - deficit));
  const protein = Math.round(Math.min(p.weightKg * 1.6, p.sex === 'male' ? 170 : 120) / 5) * 5;
  const waterL = Math.round(Math.max(2, p.weightKg * 0.033 + (p.sex === 'female' ? 0.2 : 0)) * 10) / 10;
  return { kcal, protein, waterL, steps: p.sex === 'male' ? 8000 : 7000 };
}

export function maintenance(p: { sex: Sex; weightKg: number; heightCm: number; birthYear: number; activity: Activity; scaleBmr?: number | null }): number {
  const age = new Date().getFullYear() - p.birthYear;
  return round50(bmr(p.sex, p.weightKg, p.heightCm, age, p.scaleBmr) * ACTIVITY[p.activity]);
}

/** Which portion track of the meal template fits these targets best. */
export function trackFor(t: Targets): Track {
  return t.kcal >= 1625 ? 'H' : 'A';
}

/** Water per check-in: what is left after the 500 ml wake-up glass, over three checks. */
export function waterShare(t: Partial<Targets> | undefined): string {
  const l = t?.waterL ?? 2.5;
  const ml = Math.round(((l - 0.5) * 1000) / 3 / 50) * 50;
  return `${ml} ml`;
}
